import Collections from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';

/**
 * Status lifecycle of a migration run. Mirrors MigrationRunStatus in
 * functions-22/src/db/types/migration-run.ts.
 */
const MigrationRunStatus = {
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  PARTIALLY_COMPLETED: 'PARTIALLY_COMPLETED',
  FAILED: 'FAILED',
};

const TERMINAL_STATUSES = [
  MigrationRunStatus.COMPLETED,
  MigrationRunStatus.PARTIALLY_COMPLETED,
  MigrationRunStatus.FAILED,
];

/**
 * One cross-coach workout copy, as recorded by the migrateWorkoutsToCoach endpoint.
 *
 * Read-only here — the endpoint owns the document from creation to terminal status
 * (see firestore.rules) — and observed rather than fetched, so the page follows a run's
 * progress as the server writes it. That is also what makes the run survive the page:
 * the tab holds a view of the run, not the run itself.
 */
class MigrationRun extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.MIGRATION_RUN}/${id}`, opts);
  }

  get status() {
    return this.data.status;
  }

  get total() {
    return this.data.total || 0;
  }

  get migrated() {
    return this.data.migrated || 0;
  }

  get failed() {
    return this.data.failed || 0;
  }

  get failures() {
    return this.data.failures || [];
  }

  get error() {
    return this.data.error;
  }

  get isFinished() {
    return TERMINAL_STATUSES.includes(this.status);
  }

  /**
   * When the endpoint's own timeout expires. A run the platform kills never writes a
   * terminal status, so past this an open run is one that died rather than one still
   * working — nothing else distinguishes them.
   *
   * @returns {Date|undefined} - The run's deadline.
   */
  get deadlineAt() {
    return this.data.deadlineAt?.toDate();
  }

  /**
   * An unused run doc id, so the page can start watching the run before the request that
   * creates it has answered. See BaseDocument.reserveId.
   *
   * @returns {string} - An unused migrationRun doc id.
   */
  static reserveRunId() {
    return BaseDocument.reserveId(Collections.MIGRATION_RUN);
  }
}

export default MigrationRun;
export { MigrationRunStatus };
